<template>
  <div class="vf-wrap">
    <!-- 左侧：所有字段（拖拽源） -->
    <div class="vf-left">
      <div class="vf-section-title">{{ $t('字段') }}</div>
      <a-input
        class="vf-field-search"
        v-model:value="fieldSearch"
        size="small"
        allow-clear
        :placeholder="$t('搜索字段')"
      >
        <template #prefix>
          <search-outlined />
        </template>
      </a-input>
      <div class="vf-field-pool">
        <div
          v-for="f in displayFieldRows"
          :key="f.key"
          :class="['vf-field-item', { 'vf-field-group': !f.isLeaf }]"
          :style="{ paddingLeft: `${8 + f.depth * 16}px` }"
          :draggable="f.isLeaf"
          @dragstart="f.isLeaf ? onFieldDragStart(f.option) : undefined"
          @dragend="f.isLeaf ? onDragEnd() : undefined"
          :title="f.isLeaf ? `${f.option.label}(${f.option.field})` : f.option.label"
        >
          <database-outlined
            v-if="f.isLeaf"
            class="vf-field-icon"
          />
          <span
            v-else
            class="vf-field-branch-dot"
          />
          <span class="vf-field-label">
            {{ f.isLeaf ? `${f.option.label}(${f.option.field})` : f.option.label }}
          </span>
        </div>
        <div
          v-if="displayFieldRows.length === 0"
          class="vf-empty"
        >
          {{ $t('未找到匹配字段') }}
        </div>
      </div>
    </div>

    <!-- 右侧：当前值字段配置区 -->
    <div class="vf-right">
      <div class="vf-switch-row">
        <a-switch
          v-model:checked="useExpr"
          :disabled="!hasFieldOptions"
        />
        <span class="vf-switch-label">{{ $t('启用高级表达式（字段间运算/比较/逻辑/三目）') }}</span>
      </div>

      <!-- 未启用表达式：单字段配置 -->
      <div
        v-if="!useExpr"
        class="vf-single"
      >
        <div
          class="vf-single-drop"
          :class="{ 'drag-over': dragOverSingle }"
          @dragover.prevent="onSingleDragOver"
          @dragleave="dragOverSingle = false"
          @drop.stop="onSingleDrop"
        >
          <template v-if="valueField.field">
            <span class="vf-single-field">{{ fieldLabel(valueField.field) }}</span>
            <close-outlined
              class="vf-single-remove"
              @click.stop="clearField"
            />
          </template>
          <span
            v-else
            class="vf-single-placeholder"
          >
            {{ $t('从左侧拖拽字段到此处') }}
          </span>
        </div>
        <a-select
          class="vf-single-select"
          v-model:value="valueField.field"
          size="small"
          show-search
          :filter-option="filterOption"
          :placeholder="$t('选择字段')"
          :options="
            flatFieldOptions.map(f => ({ label: `${f.label}(${f.field})`, value: f.field }))
          "
          @change="onFieldSelect"
          @click.stop
        />
        <div class="vf-tip">{{ $t('关闭表达式时，从左侧拖拽字段即可更改当前值字段') }}</div>
      </div>

      <!-- 计算表达式 + 过滤条件：折叠面板，默认两个面板全展开 -->
      <a-collapse
        v-model:activeKey="activeKeys"
        :bordered="false"
        class="vf-collapse"
      >
        <!-- 启用表达式时显示「计算表达式」面板 -->
        <a-collapse-panel
          v-if="useExpr"
          key="expr"
          :header="$t('计算表达式')"
        >
          <valueExprEditor
            v-model="valueField.expression"
            :field-options="flatFieldOptions"
            :sub-expressions="subExpressions"
            :placeholder="$t('从左侧拖拽字段，或拖拽/点击运算符、数字、字符串到此处构建表达式')"
            @change="emitChange"
          />
        </a-collapse-panel>

        <!-- 过滤条件：始终可用（仅聚合满足条件的行） -->
        <a-collapse-panel
          key="filter"
          :header="$t('过滤条件（仅聚合满足条件的行）')"
        >
          <valueExprEditor
            v-model="valueField.filter"
            :field-options="flatFieldOptions"
            :sub-expressions="subExpressions"
            :placeholder="$t('从左侧拖拽字段，构建如 销量 > 50 的过滤条件')"
            @change="emitChange"
          />
        </a-collapse-panel>
      </a-collapse>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import { DatabaseOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons-vue';
import { PivotValueField, PivotFieldOption, PivotSubExpression, ExprLocalDrag } from '../typing';
import ValueExprEditor from './valueExprEditor.vue';

interface Props {
  valueField: PivotValueField;
  fieldOptions: PivotFieldOption[];
  // 当前配置中全部子表达式（供表达式/过滤条件编辑器引用）
  subExpressions?: PivotSubExpression[];
}
const props = defineProps<Props>();
const emit = defineEmits<{ (e: 'change'): void }>();

// 跨子组件共享的拖拽负载（字段从左侧池拖出，落到编辑器画布）
const localDrag = ref<ExprLocalDrag | null>(null);
provide('vfLocalDrag', localDrag);

const flatFieldOptions = computed<PivotFieldOption[]>(() => {
  const flatten = (fields: PivotFieldOption[]): PivotFieldOption[] => {
    const list: PivotFieldOption[] = [];
    fields.forEach(field => {
      if (field.children?.length) {
        list.push(...flatten(field.children));
      } else {
        list.push({ field: field.field, label: field.label });
      }
    });
    return list;
  };
  return flatten(props.fieldOptions || []);
});

const hasFieldOptions = computed(() => flatFieldOptions.value.length > 0);

// 折叠面板展开状态：默认「计算表达式」与「过滤条件」两个面板全展开
const activeKeys = ref<string[]>(['expr', 'filter']);

// 左侧字段搜索
const fieldSearch = ref('');
const filteredFieldTree = computed<PivotFieldOption[]>(() => {
  const q = fieldSearch.value.trim().toLowerCase();
  if (!q) return props.fieldOptions;

  const filterTree = (fields: PivotFieldOption[]): PivotFieldOption[] => {
    return fields
      .map(field => {
        const selfMatch =
          field.label.toLowerCase().includes(q) || field.field.toLowerCase().includes(q);
        const children = field.children?.length ? filterTree(field.children) : [];
        if (selfMatch) {
          return {
            ...field,
            children: field.children?.length ? field.children : undefined,
          };
        }
        if (children.length) {
          return {
            ...field,
            children,
          };
        }
        if (!field.children?.length && selfMatch) {
          return { ...field };
        }
        return null;
      })
      .filter(Boolean) as PivotFieldOption[];
  };

  return filterTree(props.fieldOptions);
});

const displayFieldRows = computed<
  Array<{ key: string; depth: number; isLeaf: boolean; option: PivotFieldOption }>
>(() => {
  const list: Array<{ key: string; depth: number; isLeaf: boolean; option: PivotFieldOption }> = [];
  const walk = (fields: PivotFieldOption[], depth = 0) => {
    fields.forEach(field => {
      const isLeaf = !field.children?.length;
      list.push({
        key: `${depth}_${field.field}_${field.label}`,
        depth,
        isLeaf,
        option: field,
      });
      if (field.children?.length) {
        walk(field.children, depth + 1);
      }
    });
  };
  walk(filteredFieldTree.value);
  return list;
});

const fieldLabel = (field: string): string => {
  const f = flatFieldOptions.value.find(x => x.field === field);
  return f ? f.label : field;
};

const useExpr = computed<boolean>({
  get: () => !!props.valueField.useExpression,
  set: val => {
    props.valueField.useExpression = val;
    if (val) {
      // 启用时：若表达式为空且当前有单字段，以其为种子
      if ((props.valueField.expression || []).length === 0 && props.valueField.field) {
        props.valueField.expression = [{ type: 'field', value: props.valueField.field }];
      } else if (!props.valueField.expression) {
        props.valueField.expression = [];
      }
    } else {
      // 关闭时：若单字段为空，用表达式中首个字段兜底，避免值字段悬空
      if (!props.valueField.field) {
        const firstField = (props.valueField.expression || []).find(t => t.type === 'field');
        if (firstField) props.valueField.field = firstField.value;
      }
    }
    emitChange();
  },
});

const filterOption = (input: string, option: { label?: string }) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const emitChange = () => emit('change');

// ===== 拖拽起始（字段）=====
const onFieldDragStart = (f: PivotFieldOption) => {
  localDrag.value = { kind: 'field', field: f.field, label: f.label };
};
const onDragEnd = () => {
  localDrag.value = null;
  dragOverSingle.value = false;
};

// ===== 单字段（未启用表达式）=====
const dragOverSingle = ref(false);
const onSingleDragOver = () => {
  dragOverSingle.value = true;
};
const onSingleDrop = () => {
  const d = localDrag.value;
  dragOverSingle.value = false;
  if (d && d.kind === 'field') {
    props.valueField.field = d.field;
    props.valueField.label = d.label;
    emitChange();
  }
};
const onFieldSelect = () => {
  emitChange();
};
const clearField = () => {
  props.valueField.field = '';
  props.valueField.label = undefined;
  emitChange();
};
</script>

<style lang="less" scoped>
.vf-wrap {
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 360px;
}

.vf-left {
  width: 240px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .vf-section-title {
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 13px;
  }
  .vf-field-search {
    margin-bottom: 8px;
    flex-shrink: 0;
  }
  .vf-field-pool {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }
  .vf-field-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    margin-bottom: 4px;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    cursor: grab;
    font-size: 13px;
    transition: all 0.2s;
    user-select: none;
    &:hover {
      border-color: #1890ff;
      background: #f0f7ff;
    }
    &:active {
      cursor: grabbing;
    }
    .vf-field-icon {
      color: #1890ff;
      font-size: 12px;
    }
    .vf-field-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .vf-field-group {
    cursor: default;
    background: #fafafa;
    font-weight: 600;

    &:hover {
      border-color: #f0f0f0;
      background: #fafafa;
    }
  }
  .vf-field-branch-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d9d9d9;
    display: inline-block;
    flex-shrink: 0;
  }
  .vf-empty {
    text-align: center;
    color: #999;
    font-size: 12px;
    padding: 20px 0;
  }
}

.vf-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
}

.vf-switch-row {
  display: flex;
  align-items: center;
  gap: 8px;
  .vf-switch-label {
    font-size: 13px;
    color: #333;
  }
}

.vf-single {
  display: flex;
  flex-direction: column;
  gap: 8px;
  .vf-single-drop {
    min-height: 40px;
    border: 2px dashed #d9d9d9;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    transition: all 0.2s;
    &.drag-over {
      border-color: #1890ff;
      background: #f0f7ff;
    }
    .vf-single-field {
      font-size: 13px;
      font-weight: 600;
      color: #1890ff;
    }
    .vf-single-placeholder {
      color: #bbb;
      font-size: 12px;
    }
    .vf-single-remove {
      color: #999;
      cursor: pointer;
      &:hover {
        color: #ff4d4f;
      }
    }
  }
  .vf-single-select {
    max-width: 320px;
  }
  .vf-tip {
    font-size: 12px;
    color: #999;
  }
}

.vf-collapse {
  background: transparent;
  :deep(.ant-collapse-item) {
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    margin-bottom: 12px;
    overflow: hidden;
    background: #fff;
  }
  :deep(.ant-collapse-header) {
    font-weight: 600;
    font-size: 13px;
  }
}
</style>
